/**
 * constants.ts — Catálogos del CRM.
 *
 * REGLA: aquí solo vive lo que NO se traduce — identificadores (`value`, coinciden
 * con los valores reales en BD), iconos y metadata de agenda.
 * El texto visible sale de i18n por convención de clave:
 *   status      → t(`crm:status.${value}`)
 *   temperature → t(`crm:temperature.${value}`)
 *   activityType→ t(`crm:activityType.${value}`)
 *
 * Añadir un idioma = crear locales/<lang>/crm.json y registrarlo en i18n/config.ts.
 * NO se toca este archivo.
 */

import type { Lead } from './types'

/** Filtro de estado. 'all' es solo UI: no existe en BD. */
export const STATUS_OPTIONS = [
  { value: 'all', color: 'gray' },
  { value: 'new', color: 'yellow' },
  { value: 'contacted', color: 'blue' },
  { value: 'qualified', color: 'green' },
  { value: 'negotiating', color: 'orange' },
  { value: 'closed_won', color: 'emerald' },
  { value: 'closed_lost', color: 'red' }
]

/** Estados reales del lead (sin 'all'). El orden ES el orden de columnas del Kanban. */
export const LEAD_STATUSES: Lead['status'][] = [
  'new', 'contacted', 'qualified', 'negotiating', 'closed_won', 'closed_lost'
]

export const TEMPERATURE_OPTIONS = [
  { value: 'all', icon: '🌡️' },
  { value: 'hot', icon: '🔥' },
  { value: 'warm', icon: '☀️' },
  { value: 'cold', icon: '❄️' }
]

/**
 * Tipos de actividad + defaults de agenda (migración 034).
 * defaultDuration → minutos sugeridos · defaultBlocks → si compite por el tiempo del agente.
 * Presenciales/telefónicas bloquean; asíncronas y recordatorios no.
 * El agente puede invertir ambos por actividad (ej. visita que no bloquea porque
 * atiende dos clientes a la vez). El frontend propone, el backend respeta lo que llegue.
 */
export const ACTIVITY_TYPES = [
  { value: 'call',          icon: '📞', defaultDuration: 15, defaultBlocks: true },
  { value: 'whatsapp',      icon: '💬', defaultDuration: 5,  defaultBlocks: false },
  { value: 'email',         icon: '📧', defaultDuration: 5,  defaultBlocks: false },
  { value: 'visit',         icon: '🏠', defaultDuration: 60, defaultBlocks: true },
  { value: 'meeting',       icon: '🤝', defaultDuration: 45, defaultBlocks: true },
  { value: 'property_show', icon: '🔑', defaultDuration: 60, defaultBlocks: true },
  { value: 'follow_up',     icon: '📋', defaultDuration: 15, defaultBlocks: false },
  { value: 'note',          icon: '📝', defaultDuration: 15, defaultBlocks: false }
]

export const ACTIVITY_STATUS_OPTIONS = [
  { value: 'all' },
  { value: 'pending' },
  { value: 'overdue' },
  { value: 'completed' },
  { value: 'cancelled' }
]

/** Duraciones del selector de agenda (minutos). 'custom' abre input libre. */
export const DURATION_PRESETS = [15, 30, 45, 60, 90, 120]

/** Defaults de agenda de un tipo. Fallback seguro si el tipo no existe. */
export const getActivityTypeDefaults = (type: string) => {
  const found = ACTIVITY_TYPES.find(t => t.value === type)
  return {
    duration: found?.defaultDuration ?? 15,
    blocks: found?.defaultBlocks ?? false
  }
}

/**
 * Días sin actualizar tras los cuales un lead se considera "podrido" (rotting).
 * Patrón Pipedrive: se define POR ETAPA — un lead nuevo se enfría en días, uno en
 * negociación aguanta más. Se resetea con cualquier cambio real del lead.
 * null = esa etapa nunca se pudre (los cerrados ya no requieren acción).
 */
export const ROTTING_DAYS: Record<string, number | null> = {
  new: 3,
  contacted: 7,
  qualified: 10,
  negotiating: 14,
  closed_won: null,
  closed_lost: null
}

export const isRotting = (status: string, daysIdle?: number | null): boolean => {
  const limit = ROTTING_DAYS[status]
  return limit !== null && limit !== undefined && (daysIdle ?? 0) >= limit
}
